import { useCallback, useState, useSyncExternalStore } from "react";
const GROUP_BY_KEY = 'swim-lab:group-by';
const FILTERS_COLLAPSED_KEY = 'swim-lab:filters-collapsed';
/**
 * Builds a hook for a filter preference that every page reads from one place and
 * mirrors to localStorage.
 *
 * Shared rather than held per-component: the swimmer tabs are hidden with `display: none`
 * instead of unmounting, so each one keeps a live copy of this state and they would
 * otherwise drift apart as you switch between them.
 *
 * `parse` runs at module load, so it must cope with localStorage access failing outright
 * under some private-browsing / blocked-storage settings - it is handed `null` in that case.
 */
function createSharedPreference(key, parse, serialise) {
    let current;
    try {
        current = parse(localStorage.getItem(key));
    }
    catch {
        current = parse(null);
    }
    const listeners = new Set();
    const subscribe = (listener) => {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    };
    const getSnapshot = () => current;
    return function usePreference() {
        const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
        const setValue = useCallback((next) => {
            if (next === current)
                return;
            current = next;
            try {
                localStorage.setItem(key, serialise(next));
            }
            catch {
                // Preference just won't survive a reload.
            }
            listeners.forEach(listener => listener());
        }, []);
        return [value, setValue];
    };
}
export function useCourseFilter() {
    return useState('all');
}
export const useGroupBy = createSharedPreference(GROUP_BY_KEY, raw => (raw === 'stroke' ? 'stroke' : 'course'), value => value);
/**
 * Whether the shared filter bar is folded away. Only honoured on phone-width screens,
 * where the bar stacks into one full width row per control - see `styes.css`.
 */
export const useFiltersCollapsed = createSharedPreference(FILTERS_COLLAPSED_KEY, raw => raw === 'true', value => String(value));
export const courseLabel = (course) => course === '50m' ? 'Long Course (50m)' : 'Short Course (25m)';
// Group headings read as a course description when grouping by course,
// and as the stroke name listed when grouping by stroke.
export const groupLabel = (groupBy, groupKey) => groupBy === 'course' ? courseLabel(groupKey) : groupKey;
